import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Save } from "lucide-react";
import { fetchPlatformDemoEvent, updatePlatformDemoEvent } from "@/lib/platform-api";

const SLABS = [
  { id: "hero", label: "Hero" },
  { id: "trust", label: "Trust strip" },
  { id: "pillars", label: "Pillars" },
  { id: "venue", label: "Venue CTA" },
  { id: "cta", label: "Register CTA" },
];

function Field({ label, value, onChange, multiline }) {
  return (
    <label className="block">
      <span className="text-xs text-[var(--text-tertiary)]">{label}</span>
      {multiline ? (
        <textarea className="input-dark w-full mt-1 min-h-[80px] text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className="input-dark w-full mt-1 text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}

function SlabEditor({ slabId, content, onChange }) {
  const patch = (key, val) => onChange({ ...content, [key]: val });

  if (slabId === "hero") {
    const h = content.hero || {};
    return (
      <div className="space-y-4">
        <Field label="Eyebrow" value={h.eyebrow} onChange={(v) => patch("hero", { ...h, eyebrow: v })} />
        <Field label="Subtitle" value={h.subtitle} onChange={(v) => patch("hero", { ...h, subtitle: v })} multiline />
        <p className="text-xs text-[var(--text-tertiary)]">Event title and dates come from the global demo event record (evt_001).</p>
      </div>
    );
  }
  if (slabId === "trust") {
    const t = content.trust || {};
    return (
      <div className="space-y-4">
        <Field label="Label" value={t.label} onChange={(v) => patch("trust", { ...t, label: v })} />
        <Field
          label="Sponsors (comma-separated)"
          value={(t.sponsors || []).join(", ")}
          onChange={(v) => patch("trust", { ...t, sponsors: v.split(",").map((s) => s.trim()).filter(Boolean) })}
        />
      </div>
    );
  }
  if (slabId === "pillars") {
    const p = content.pillars || {};
    return (
      <div className="space-y-4">
        <Field label="Overline" value={p.overline} onChange={(v) => patch("pillars", { ...p, overline: v })} />
        <Field label="Title line 1" value={p.title} onChange={(v) => patch("pillars", { ...p, title: v })} />
        <Field label="Title line 2" value={p.titleLine2} onChange={(v) => patch("pillars", { ...p, titleLine2: v })} />
        <p className="text-xs text-[var(--text-tertiary)]">Edit pillar cards in the JSON editor below.</p>
      </div>
    );
  }
  if (slabId === "venue") {
    const v = content.venue || {};
    return (
      <div className="space-y-4">
        <Field label="Headline" value={v.title} onChange={(val) => patch("venue", { ...v, title: val })} />
        <Field label="Body" value={v.body} onChange={(val) => patch("venue", { ...v, body: val })} multiline />
      </div>
    );
  }
  if (slabId === "cta") {
    const c = content.cta || {};
    return (
      <div className="space-y-4">
        <Field label="Button label" value={c.label} onChange={(v) => patch("cta", { ...c, label: v })} />
        <Field label="Hint under buttons" value={c.hint} onChange={(v) => patch("cta", { ...c, hint: v })} />
      </div>
    );
  }
  return null;
}

export default function PlatformDemoEvent() {
  const [content, setContent] = useState(null);
  const [slug, setSlug] = useState("stride-2026");
  const [activeSlab, setActiveSlab] = useState("hero");
  const [jsonText, setJsonText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPlatformDemoEvent();
      setContent(data.content);
      setSlug(data.slug || "stride-2026");
      setJsonText(JSON.stringify(data.content, null, 2));
    } catch (err) {
      toast.error("Could not load demo event CMS", { description: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      let payload = content;
      try {
        payload = JSON.parse(jsonText);
      } catch {
        throw new Error("JSON editor has invalid syntax.");
      }
      const data = await updatePlatformDemoEvent(payload);
      setContent(data.content);
      setJsonText(JSON.stringify(data.content, null, 2));
      toast.success("Demo event page published", {
        description: `Live at /e/${slug}`,
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading || !content) {
    return <p className="p-8 text-sm text-[var(--text-secondary)]">Loading demo event editor…</p>;
  }

  return (
    <div className="p-8 lg:p-12 max-w-[1200px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-overline">Platform</div>
          <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Global demo event</h1>
          <p className="mt-3 text-sm text-[var(--text-secondary)] max-w-xl">
            Controls the public page at <code className="font-mono text-xs">/e/{slug}</code>. Always dark mode;
            does not affect Therme or other workspaces. Sessions and speakers are seeded automatically.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={`https://aivoryems.com/e/${slug}`}
            target="_blank"
            rel="noreferrer"
            className="btn-secondary text-sm"
          >
            Preview <ExternalLink size={14} />
          </a>
          <button type="button" className="btn-primary text-sm" onClick={save} disabled={busy}>
            <Save size={14} /> {busy ? "Saving…" : "Publish"}
          </button>
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-[220px_1fr] gap-6">
        <nav className="flex lg:flex-col gap-1 flex-wrap">
          {SLABS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setActiveSlab(s.id)}
              className={`px-3 py-2 text-sm text-left rounded-sm ${
                activeSlab === s.id ? "bg-[var(--bg-elevated)]" : "text-[var(--text-secondary)]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </nav>
        <div className="surface p-6">
          <SlabEditor
            slabId={activeSlab}
            content={content}
            onChange={(next) => {
              setContent(next);
              setJsonText(JSON.stringify(next, null, 2));
            }}
          />
        </div>
      </div>

      <div className="mt-10 surface p-6">
        <h2 className="font-heading text-lg mb-2">Advanced · full JSON</h2>
        <textarea
          className="input-dark w-full min-h-[280px] font-mono text-xs"
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
        />
      </div>
    </div>
  );
}
